'use client';

import { useRef, useEffect } from 'react';
import { useMagnetic } from '@/lib/hooks/useMagnetic';
import { HeroStatusLine } from '@/components/effects/HeroStatusLine';
import { useLocale } from '@/lib/i18n/LocaleContext';

// Hover effect for title letters. GSAP is injected so this module doesn't
// pull GSAP into the homepage entry chunk (TASK-4).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyHoverEffect(letters: NodeListOf<Element>, gsap: any) {
  const spansArray = Array.from(letters);

  const handleMouseLeave = () => {
    spansArray.forEach((span) => {
      gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
    });
  };

  const handleMouseMoveObj = (e: Event) => {
    const mouseEvent = e as MouseEvent;
    const hoveredSpan = mouseEvent.target as HTMLElement;
    const rect = hoveredSpan.getBoundingClientRect();
    const mouseX = mouseEvent.clientX - rect.left;
    const scaleFactor = 0.2;
    const center = rect.width / 2;

    let scale: number;
    if (mouseX < center) {
      scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
    } else {
      scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
    }

    gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

    const hoveredIndex = spansArray.indexOf(hoveredSpan);
    const prevSpan = spansArray[hoveredIndex - 1];
    const nextSpan = spansArray[hoveredIndex + 1];

    if (prevSpan) {
      const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
      const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
      gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
    }

    if (nextSpan) {
      const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
      const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
      gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
    }
  };

  spansArray.forEach((span) => {
    span.addEventListener('mousemove', handleMouseMoveObj);
    span.addEventListener('mouseleave', handleMouseLeave);
  });
}

export function HeroSection() {
  const { dict, dir } = useLocale();
  const rtl = dir === 'rtl';
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLDivElement>(null);
  const subtitleContainerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLButtonElement>(null);
  const magneticRef = useMagnetic<HTMLDivElement>(0.3);

  useEffect(() => {
    if (!heroRef.current) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    // Set up subtitle mask tracking (mouse move) — coalesced via rAF so the
    // handler can't burn the main thread on hover-heavy displays.
    let mousePending = false;
    let lastX = 0;
    let lastY = 0;
    const handleMouseMove = (e: MouseEvent) => {
      lastX = e.clientX;
      lastY = e.clientY;
      if (mousePending) return;
      mousePending = true;
      requestAnimationFrame(() => {
        mousePending = false;
        if (subtitleContainerRef.current) {
          const subRect = subtitleContainerRef.current.getBoundingClientRect();
          subtitleContainerRef.current.style.setProperty('--mask-x', `${lastX - subRect.left}px`);
          subtitleContainerRef.current.style.setProperty('--mask-y', `${lastY - subRect.top}px`);
        }
      });
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    let ctx: { revert: () => void } | undefined;
    let cancelled = false;

    (async () => {
      const { gsap } = await import('@/lib/gsap');
      if (cancelled) return;

      ctx = gsap.context(() => {
      // Hero is rendered visible by default so the H1 is LCP-eligible without
      // waiting on JS. The shuffle animation is a decorative overlay we add
      // *after* paint, gated on reduced-motion.
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters && !prefersReducedMotion) {
        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);
        const letterTimeline = gsap.timeline();
        shuffledLetters.forEach((letter, index) => {
          letterTimeline.fromTo(
            letter,
            { scaleY: 0.5 },
            { duration: 0.5, scaleY: 1, ease: 'power3.out' },
            index * 0.04,
          );
        });
        letterTimeline.call(() => {
          applyHoverEffect(letters, gsap);
        });
      } else if (letters) {
        applyHoverEffect(letters, gsap);
      }

      // Subtitle: visible by default; only a small slide-in enhancement if motion is OK
      if (subtitleRef.current && !prefersReducedMotion) {
        gsap.fromTo(
          subtitleRef.current,
          { y: 16, opacity: 0.25 },
          { y: 0, opacity: 0.5, duration: 0.6, ease: 'power3.out' },
        );
      }

      // Scroll indicator: visible by default; gentle nudge if motion is OK
      if (scrollRef.current && !prefersReducedMotion) {
        gsap.fromTo(
          scrollRef.current,
          { y: 12, opacity: 0.4 },
          { y: 0, opacity: 1, duration: 0.7, ease: 'power2.out' },
        );
      }

      // Scroll parallax on the whole hero content (parallax-scroll-caption)
      // Title and content move up faster than background when scrolling
      if (heroRef.current) {
        const contentEl = heroRef.current.querySelector('.hero-content-wrap');
        if (contentEl) {
          gsap.to(contentEl, {
            yPercent: -30,
            ease: 'none',
            scrollTrigger: {
              trigger: heroRef.current,
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            },
          });
        }
      }

      return undefined;
    }, heroRef);
    })();

    return () => {
      cancelled = true;
      window.removeEventListener('mousemove', handleMouseMove);
      ctx?.revert();
    };
  }, []);

  const title = dict.hero.title;

  return (
    <section
      ref={heroRef}
      id="hero"
      className="relative flex min-h-[100dvh] w-full max-w-[100vw] items-center justify-center overflow-x-hidden overflow-y-visible bg-transparent"
    >
      {/* Main Content - Centered but pushed down slightly */}
      <div className="hero-content-wrap relative z-10 flex flex-col items-center justify-center text-center px-4 md:px-12 lg:px-16 mt-[5vh] md:mt-[15vh]">
        {/* Hero Title - DATACODE. Always the Latin brand word, in every
            locale, so it stays LTR and in Six Caps (never translated,
            never handed to the Cairo/RTL fallback below). Per-character
            spans are safe here specifically because the content is always
            Latin, this pattern must not be copied for translated text. */}
        <h1
          ref={titleRef}
          dir="ltr"
          aria-label={title}
          className="hero-title keep-latin font-six-caps text-[clamp(68px,18vw,134px)] sm:text-[clamp(96px,17vw,168px)] md:text-[189px] lg:text-[calc(1rem+20vw)] leading-[0.75] text-white lowercase overflow-visible whitespace-nowrap"
        >
          {title.split('').map((char, i) => (
            <span
              key={i}
              aria-hidden="true"
              className="letter inline-block"
              style={{ transformOrigin: 'center bottom' }}
            >
              {char}
            </span>
          ))}
        </h1>

        {/* Explicit spacer to force gap regardless of h1 bounding box bleeding */}
        <div className="h-6 md:h-12 w-full flex-shrink-0" />

        {/* Hero Subtitle. Arabic and Sorani get one plain, always-readable
            paragraph: the dim-until-spotlight reveal below is unreadable in
            Arabic script and its forced line breaks fight the shaping. */}
        {rtl ? (
          <p className="relative max-w-[90vw] md:max-w-2xl text-center mt-4 md:mt-0 px-4 text-base md:text-xl font-normal text-white/85 leading-[1.9]">
            {dict.hero.subtitleMobile}
          </p>
        ) : (
          <>
        <div
          ref={subtitleContainerRef}
          className="relative max-w-[90vw] md:max-w-3xl text-center cursor-default group mt-4 md:mt-0"
        >
          {/* Mobile Text - Always visible, standard elegant font */}
          <div className="md:hidden text-sm sm:text-base font-light tracking-wide text-white/80 leading-relaxed px-4">
            {dict.hero.subtitleMobile}
          </div>

          {/* Desktop Base Text - visible initially, elegant and slightly dim */}
          <div
            ref={subtitleRef}
            className="hidden md:block text-base md:text-lg lg:text-xl font-light tracking-wide leading-normal text-white/40 md:leading-snug"
          >
            {dict.hero.subtitleDesktopLine1}<br />
            {dict.hero.subtitleDesktopLine2}<br />
            {dict.hero.subtitleDesktopLine3}
          </div>

          {/* Desktop Revealed Text - visible only under the spotlight mask */}
          <div
            className="hidden md:block absolute inset-0 text-base md:text-lg lg:text-xl font-medium tracking-wide leading-normal text-white md:leading-snug pointer-events-none opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{
              WebkitMaskImage: `radial-gradient(circle 140px at var(--mask-x, -999px) var(--mask-y, -999px), black 20%, transparent 80%)`,
              maskImage: `radial-gradient(circle 140px at var(--mask-x, -999px) var(--mask-y, -999px), black 20%, transparent 80%)`,
            }}
          >
            {dict.hero.subtitleRevealLine1}<br />
            {dict.hero.subtitleRevealLine2}<br />
            {dict.hero.subtitleRevealLine3}
          </div>
        </div>
          </>
        )}
      </div>

      {/* Scroll Indicator - Bottom Left (magnetic wrapper pulls toward cursor) */}
      <div ref={magneticRef} className="absolute bottom-8 left-8 md:left-20">
        <button
          ref={scrollRef}
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
        >
          <span className="text-sm tracking-wider text-white/60">{dict.hero.scrollToExplore}</span>
          <svg
            className="h-4 w-4 text-white/60"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {/* Live studio status - Bottom Right, desktop only */}
      <HeroStatusLine className="absolute bottom-8 right-8 md:right-20 hidden md:flex" />

    </section>
  );
}
